/**
 * RewardFulfillmentClaimForm
 *
 * Inline shipping/contact form embedded in the platform PrizeWonModal
 * when a winner's reward is type=MERCH OR (type=GIFT_CARD &&
 * !tilloProductId). Replaces the previous Google-Form-link pattern.
 *
 * Built on platform-shared input components so brand modals inherit
 * styling without per-form SCSS overrides:
 *   - `AddressInputGroup` for the address block (autocomplete +
 *     manual + per-country state/zip handling)
 *   - `the legacy select` for the address-type dropdown
 *   - `the legacy button` for the submit action; parent passes `brandColor`
 *     to override the default purple via forwardProps.style — same
 *     pattern BrandChallengePrizeWonModal uses for its Confirm button
 *
 * Phone is captured as a plain free-text string — ops contacts the
 * winner by email and uses phone only as a backup contact, so no
 * dial-code picker or format coercion is needed.
 *
 * The form's internal Address shape uses the platform `Address` type
 * (line1/line2/locality/administrativeArea/postalCode/country). On
 * submit, we flatten to the backend's named fields (addressLine1/etc.)
 * so the swagger contract stays explicit.
 *
 * Idempotency: server enforces one claim per (user, prizePoolId) OR
 * per (user, distributedRewardId) depending on which anchor the
 * parent passed in; either path returns 409 on duplicate which we
 * surface as "already submitted for this prize."
 */
import React, { ButtonHTMLAttributes, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Address, ApiError, RewardFulfillmentClaimBodyAddressTypeEnum } from "@devour/client";
import { IStore } from "@/redux/defaultStore";
import { useGetUserProfile } from "@/hooks/useGetUserProfile";
import { useSubmitFulfillmentClaim } from "@/hooks/useSubmitFulfillmentClaim";
import { getApiErrorMessage } from "@/utils/getApiErrorMessage";
import AddressInputGroup from "@/components/inputs/AddressInputGroup";
import Select from "@/components/inputs/Select";
import {Button} from "@/components/buttons/Button";

interface Props {
    /*
     * Exactly one of prizePoolId or distributedRewardId must be provided.
     * prizePoolId = challenge/raffle prize-pool wins.
     * distributedRewardId = wheel-spin / gamification / manual-grant wins.
     *
     * Two separate optional props (rather than one polymorphic
     * `anchor: { type, id }` field) because the generated @devour/client
     * body type exposes both as optional explicit properties matching
     * the swagger schema. A polymorphic shape would force a mapping
     * step at every call site and diverge from the generated type.
     */
    prizePoolId?: string;
    distributedRewardId?: string;
    rewardName: string;
    /**
     * Brand color forwarded to the submit `the legacy button` so the CTA
     * matches the surrounding modal chrome (Minelab red, Polaris
     * orange, BrandChallenge per-brand). Falls back to platform purple.
     */
    brandColor?: string;
    /**
     * Optional brand-namespace class applied to the outer `<form>` (and
     * success state). Lets the parent modal scope layout overrides
     * (e.g. `brand-challenge_prize-modal_body_fulfillment-form`) without
     * forking the component.
     */
    className?: string;
    /**
     * Fires when the submission succeeds. Parents can use this to flip
     * the modal copy ("we'll be in touch") or auto-close after a delay.
     */
    onSubmitted?: () => void;
    /**
     * Optional handler for an inline "See Official Rules" link rendered
     * in the intro paragraph. When omitted, the link is not shown. Parents
     * own the rules surface.
     */
    onOfficialRulesClick?: () => void;
}

/*
 * Mirror of the server-side ZIP regex in post_rewards_fulfillment_claim.
 * Used by isFormValid() so the Submit button stays disabled when an
 * autocomplete-filled non-US postal code is sitting in the field.
 */
const US_ZIP_RE = /^\d{5}(-\d{4})?$/;

const defaultAddress: Address = {
    line1: "",
    line2: "",
    locality: "",
    administrativeArea: "",
    postalCode: "",
    country: "US",
};

export const RewardFulfillmentClaimForm: React.FC<Props> = ({
    prizePoolId,
    distributedRewardId,
    rewardName,
    brandColor,
    className,
    onSubmitted,
    onOfficialRulesClick,
}) => {
    // authStore.currentUser is not reliably populated on hub pages — user comes from React Query.
    const fullToken = useSelector((s: IStore) => s.authStore.fullToken);
    const { data: currentUser } = useGetUserProfile(fullToken);
    const userEmail = currentUser?.user?.email ?? "";
    const userFullName = `${currentUser?.user?.firstName ?? ""} ${currentUser?.user?.lastName ?? ""}`.trim();

    const [fullName, setFullName] = useState(userFullName);
    const [email, setEmail] = useState(userEmail);
    const [phone, setPhone] = useState("");

    /*
     * Pre-fill name + email sync once the React Query profile resolves.
     * useState's initial value is captured on mount when currentUser is
     * still undefined — without these effects the fields stay empty and
     * the winner has to retype info we already have. Guarded by `!fullName`
     * / `!email` so we don't clobber typing in progress.
     */
    useEffect(() => {
        if (userFullName && !fullName) setFullName(userFullName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userFullName]);
    useEffect(() => {
        if (userEmail && !email) setEmail(userEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userEmail]);
    const [address, setAddress] = useState<Address>(defaultAddress);
    const [addressType, setAddressType] = useState<RewardFulfillmentClaimBodyAddressTypeEnum>(RewardFulfillmentClaimBodyAddressTypeEnum.RESIDENTIAL);
    const [notes, setNotes] = useState("");
    const [submittedId, setSubmittedId] = useState<string | null>(null);
    // Parsing the backend message is async, so the message is state, not derived.
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const submit = useSubmitFulfillmentClaim();

    function isFormValid(): boolean {
        if (!fullName.trim()) return false;
        if (!email.trim()) return false;
        if (!phone.trim()) return false;
        if (!address.line1?.trim()) return false;
        if (!address.locality?.trim()) return false;
        if (!address.administrativeArea?.trim()) return false;
        /*
         * Mirror the server-side ZIP format regex so Submit stays
         * disabled when an autocomplete-filled international postal
         * code is in the field after the user switches country back to
         * US. Avoids a server 400 round-trip on edge-case input.
         */
        if (!US_ZIP_RE.test((address.postalCode ?? "").trim())) return false;
        return true;
    }

    async function handleSubmit(e: React.FormEvent): Promise<void> {
        e.preventDefault();
        if (!isFormValid() || submit.isPending) return;
        setErrorMsg(null);
        try {
            /*
             * Map the platform Address shape to the backend's flat
             * named fields (addressLine1/city/state/zipCode) so the
             * swagger contract stays explicit.
             */
            const res = await submit.mutateAsync({
                prizePoolId,
                distributedRewardId,
                fullName: fullName.trim(),
                email: email.trim(),
                phone: phone.trim(),
                addressLine1: (address.line1 ?? "").trim(),
                addressLine2: (address.line2 ?? "").trim() || undefined,
                city: (address.locality ?? "").trim(),
                state: (address.administrativeArea ?? "").trim(),
                zipCode: (address.postalCode ?? "").trim(),
                addressType,
                notes: notes.trim() || undefined,
            });
            setSubmittedId(res.id);
            onSubmitted?.();
        } catch (err) {
            /*
             * Read the backend's own message. getApiErrorMessage unwraps the
             * ResponseError and parses the body; reading `.message` off the
             * error instead yields the client's constant "Response returned an
             * error code", which is what this form would otherwise show in
             * place of the real validation failure. Async, so the message is
             * state rather than derived from submit.error.
             */
            setErrorMsg(await getApiErrorMessage(err as { json: () => Promise<ApiError> }));
        }
    }

    const submitStyle = brandColor ? { backgroundColor: brandColor, width: "100%" } : { width: "100%" };

    /*
     * Compose the className so parent brand-namespace classes (e.g.
     * `ml-rttr__prize-won-modal_fulfillment-form`) can scope layout
     * overrides without forking the form. Used on both the success and
     * editable states so the parent's spacing/padding applies in both.
     */
    const rootClass = ["fulfillment-claim-form", className].filter(Boolean).join(" ");
    const successClass = ["fulfillment-claim-form", "fulfillment-claim-form--success", className].filter(Boolean).join(" ");

    if (submittedId) {
        return (
            <div className={successClass}>
                <h3>Shipping info saved.</h3>
                <p>
                    Thanks! We've passed your details along — the fulfillment team will reach
                    out by email to coordinate delivery of your <strong>{rewardName}</strong>.
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={(e) => void handleSubmit(e)} className={rootClass} noValidate>
            <p className="fulfillment-claim-form__intro">
                To claim your {rewardName}, confirm you&apos;re a US resident by entering
                your US shipping address below. Prize fulfillment is available to
                eligible US residents only.
                {onOfficialRulesClick &&
                    <>
                        {" "}
                        <button
                            type="button"
                            className="fulfillment-claim-form__rules-link"
                            onClick={onOfficialRulesClick}
                        >
                            See Official Rules
                        </button>
                    </>
                }
            </p>

            <div className="fulfillment-claim-form__field">
                <label>Full Name</label>
                <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                    autoComplete="name"
                />
            </div>

            <div className="fulfillment-claim-form__field">
                <label>Email</label>
                <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                />
            </div>

            <div className="fulfillment-claim-form__field">
                <label>Phone Number</label>
                <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                    autoComplete="tel"
                    placeholder="Backup contact only"
                    maxLength={30}
                />
            </div>

            <AddressInputGroup
                value={address}
                onChange={setAddress}
                defaultMode="autocomplete"
            />

            <div className="fulfillment-claim-form__field">
                <label>Address Type</label>
                <Select
                    value={addressType}
                    onChange={(e) => setAddressType(e.target.value as RewardFulfillmentClaimBodyAddressTypeEnum)}
                    required
                >
                    <option value={RewardFulfillmentClaimBodyAddressTypeEnum.RESIDENTIAL}>Residential</option>
                    <option value={RewardFulfillmentClaimBodyAddressTypeEnum.BUSINESS}>Business</option>
                </Select>
            </div>

            <div className="fulfillment-claim-form__field">
                <label>Delivery Notes (optional)</label>
                <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    placeholder="Gate code, leave with neighbor, etc."
                    maxLength={1000}
                />
            </div>

            {errorMsg && <p className="fulfillment-claim-form__error">{errorMsg}</p>}

            <Button variant="primary"
                loading={submit.isPending}
                {...{type: "submit",
                    disabled: !isFormValid() || submit.isPending,
                    style: submitStyle}}
            >
                {submit.isPending ? "Submitting..." : "Submit Shipping Info"}
            </Button>
        </form>
    );
};

export default RewardFulfillmentClaimForm;
